// src/components/LegalPage.tsx: shared chrome and typography for the public
// legal documents (/privacy, /terms). Standalone, outside AppLayout, no auth.
import React from 'react';
import { Link } from 'react-router-dom';
import { C } from '../lib/applications';

const bodyStyle: React.CSSProperties = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: '14px', color: C.textSec, lineHeight: 1.7, margin: '0 0 12px',
};

/** A numbered section heading, e.g. "3.1 Data you provide directly". */
export const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 style={{
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '11px', color: C.muted, fontWeight: 700,
    letterSpacing: '1px', textTransform: 'uppercase', margin: '32px 0 12px',
  }}>{children}</h2>
);

export const P = ({ children }: { children: React.ReactNode }) => (
  <p style={bodyStyle}>{children}</p>
);

/** Gold em-dash bullets on hairline rows, matching the platform list style. */
export const List = ({ items }: { items: React.ReactNode[] }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
    {items.map((item, i) => (
      <li key={i} style={{
        ...bodyStyle, margin: 0,
        padding: '6px 0', borderBottom: '1px solid #F0F0F0',
        display: 'flex', gap: '10px',
      }}>
        <span style={{ color: C.gold, flexShrink: 0 }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const Mail = () => (
  <a href="mailto:hello@altveno.com" style={{ color: C.gold, textDecoration: 'none' }}>
    hello@altveno.com
  </a>
);

interface LegalPageProps {
  title: string;
  updated: string;
  children: React.ReactNode;
}

export default function LegalPage({ title, updated, children }: LegalPageProps) {
  return (
    <div style={{
      minHeight: '100vh', background: C.surface,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{
        background: C.navy, padding: '18px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
            <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#5dc9a8" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
            <circle cx="14" cy="14" r="2.2" fill={C.gold} />
          </svg>
          <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>
            Altveno
          </span>
        </div>
        <Link to="/dashboard" style={{ color: C.gold, fontSize: '13px', textDecoration: 'none' }}>
          Back to Platform
        </Link>
      </div>

      <div style={{
        maxWidth: '800px', margin: '40px auto', background: C.white,
        borderRadius: '4px', border: `1px solid ${C.border}`, padding: '48px',
      }}>
        <h1 style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: '32px', color: C.navy, fontWeight: 400, margin: '0 0 4px',
        }}>{title}</h1>
        <div style={{
          fontSize: '12px', color: C.muted,
          marginBottom: '40px', paddingBottom: '24px',
          borderBottom: `1px solid ${C.borderLt}`,
        }}>
          Altveno Ltd &nbsp;·&nbsp; Last updated: {updated}
        </div>

        {children}

        <div style={{
          borderTop: `1px solid ${C.borderLt}`, marginTop: '40px',
          paddingTop: '20px', textAlign: 'center',
          fontSize: '12px', color: C.muted,
        }}>
          Questions? Contact us at <Mail />
        </div>
      </div>
    </div>
  );
}
